import { Fragment, type ReactNode } from "react";
import { cn, tv } from "tailwind-variants";
import type { ApiBlockListModel } from "#/_generated/umbraco/delivery-api/models";
import {
	toHoverItemCssClasses,
	toHoverMediaCssClasses,
} from "#/_packages/umbrella/integrations/umbraco/utils/animation-utils.ts";
import { RenderBlock } from "#/_packages/umbrella/integrations/umbraco/utils/blockgrid-item-renderer.tsx";
import type {
	Alignment,
	AnimationScrollType,
	BoxVariant,
	ImageMedia,
	VerticalSelfAlignment,
} from "#/_packages/umbrella/types";
import { getAltTextFromString } from "#/_packages/umbrella/utils/image-utils.ts";

export type ContactCardOrientation =
	| "orientation-horizontal"
	| "orientation-vertical";

export type ContactCardDetailType =
	| "name"
	| "role"
	| "jobTitle"
	| "email"
	| "phone"
	| "mobile"
	| "website"
	| "company"
	| "department"
	| "other";

export type ContactCardDetail = {
	type: ContactCardDetailType;
	value: string;
};

export type ContactCardImage = ImageMedia & {
	isSvg?: boolean;
	webpSrc?: string;
	jpegSrc?: string;
};

export type ContactCardProps = {
	id?: string | null;
	image?: ContactCardImage;
	details: readonly ContactCardDetail[];
	description?: ReactNode;
	links?: ApiBlockListModel;
	orientation?: ContactCardOrientation;
	contentAlignment?: Alignment;
	boxVariant?: BoxVariant;
	alignmentSelfVertical?: VerticalSelfAlignment;
	fullHeight?: boolean;
	animationHoverType?: string[];
	animationScrollType?: AnimationScrollType;
	className?: string;
};

const contactCard = tv({
	slots: {
		root: "block__contact-card flex w-full gap-5",
		figure: "figure m-0 shrink-0",
		image:
			"figure-img w-28 aspect-square rounded-[var(--umb-component-border-radius)] object-fit-cover transition-all duration-200",
		body: "contact-card-body flex min-w-0 flex-1 flex-col gap-3 overflow-hidden",
		details:
			"contact-card-details flex flex-col gap-1 overflow-hidden text-sm text-text-muted",
		name: "contact-card-name text-xl font-bold text-text-base",
		title: "contact-card-title text-sm font-medium text-text-muted",
		email: "contact-card-email text-truncate truncate text-sm text-text-muted",
		phone: "contact-card-phone text-sm text-text-muted",
		website:
			"contact-card-website text-truncate truncate text-sm text-text-muted",
		detail: "contact-card-detail text-sm text-text-muted",
		description:
			"contact-card-description mb-0-last text-sm leading-6 text-text-muted",
		footer: "contact-card-footer flex flex-wrap gap-2 overflow-hidden",
		link: "text-link hover:underline",
	},
	variants: {
		orientation: {
			"orientation-horizontal": {
				root: "orientation-horizontal flex-col sm:flex-row",
			},
			"orientation-vertical": {
				root: "orientation-vertical flex-col",
			},
		},
		contentAlignment: {
			start: { root: "content-alignment-start text-left" },
			center: {
				root: "content-alignment-center text-center",
				footer: "justify-center",
			},
			end: {
				root: "content-alignment-end text-right",
				footer: "justify-end",
			},
			stretch: { root: "" },
		},
		boxVariant: {
			none: { root: "" },
			background: {
				root: "card card-body card-background rounded-[var(--umb-border-radius-lg)] bg-surface-muted p-6",
			},
			outline: {
				root: "card card-body card-outline rounded-[var(--umb-border-radius-lg)] border border-border-base p-6",
			},
			shadow: {
				root: "card card-body card-shadow rounded-[var(--umb-border-radius-lg)] border border-border-base bg-surface-base p-6 shadow-md",
			},
		},
		fullHeight: {
			true: { root: "h-full h-full" },
			false: { root: "" },
		},
		alignmentSelfVertical: {
			stretch: { root: "" },
			start: { root: "self-start" },
			center: { root: "self-center" },
			end: { root: "self-end" },
		},
		animationScrollType: {
			none: { root: "" },
			"anim-view-fadeIn-moveUp": { root: "anim-view-fadeIn-moveUp" },
			"anim-view-fadeIn-moveDown": { root: "anim-view-fadeIn-moveDown" },
			"anim-view-fadeIn-moveLeft": { root: "anim-view-fadeIn-moveLeft" },
		},
	},
	defaultVariants: {
		orientation: "orientation-vertical",
		contentAlignment: "start",
		boxVariant: "none",
		fullHeight: false,
		alignmentSelfVertical: "start",
		animationScrollType: "none",
	},
});

function formatWebsiteDisplay(url: string) {
	return url.replace(/^https?:\/\//i, "").toLowerCase();
}

function renderDetail(
	detail: ContactCardDetail,
	classes: ReturnType<typeof contactCard>,
) {
	switch (detail.type) {
		case "name":
			return <div className={classes.name()}>{detail.value}</div>;
		case "role":
		case "jobTitle":
			return <div className={classes.title()}>{detail.value}</div>;
		case "email":
			return (
				<div className={classes.email()} title={detail.value}>
					<a href={`mailto:${detail.value}`} className={classes.link()}>
						{detail.value}
					</a>
				</div>
			);
		case "phone":
		case "mobile":
			return (
				<div className={classes.phone()}>
					<a href={`tel:${detail.value}`} className={classes.link()}>
						{detail.value}
					</a>
				</div>
			);
		case "website":
			return (
				<div className={classes.website()} title={detail.value}>
					<a
						href={detail.value}
						target="_blank"
						rel="noopener noreferrer"
						className={classes.link()}
					>
						{formatWebsiteDisplay(detail.value)}
					</a>
				</div>
			);
		case "company":
		case "department":
		case "other":
		default:
			return <div className={classes.detail()}>{detail.value}</div>;
	}
}

export function ContactCard({
	id,
	image,
	details,
	description,
	links,
	orientation = "orientation-vertical",
	contentAlignment = "start",
	boxVariant = "none",
	alignmentSelfVertical = "start",
	fullHeight = false,
	animationHoverType = [],
	animationScrollType = "none",
	className,
}: ContactCardProps) {
	const classes = contactCard({
		orientation,
		contentAlignment,
		boxVariant,
		alignmentSelfVertical,
		fullHeight,
		animationScrollType,
	});
	const hoverItemClasses = toHoverItemCssClasses(animationHoverType);
	const hoverMediaClasses = toHoverMediaCssClasses(animationHoverType);
	const visibleDetails = details.filter(
		(detail) => detail.value.trim().length > 0,
	);

	return (
		<div
			id={id ?? undefined}
			className={cn(classes.root(), hoverItemClasses, className)}
		>
			{image ? (
				<figure className={cn(classes.figure(), hoverMediaClasses)}>
					{image.isSvg ? (
						<img
							className={cn(classes.image(), "w-full object-fit-contain")}
							src={image.src}
							width="1"
							height="1"
							loading="lazy"
							alt={getAltTextFromString(image.alt)}
						/>
					) : (
						<picture>
							{image.webpSrc ? (
								<source srcSet={image.webpSrc} type="image/webp" />
							) : null}
							{image.jpegSrc ? (
								<source srcSet={image.jpegSrc} type="image/jpeg" />
							) : null}
							<img
								className={cn(classes.image(), "ratio")}
								src={image.src}
								width="1"
								height="1"
								loading="lazy"
								alt={getAltTextFromString(image.alt)}
							/>
						</picture>
					)}
				</figure>
			) : null}
			<div className={classes.body()}>
				<div className={classes.details()}>
					{visibleDetails.map((detail) => (
						<Fragment key={`${detail.type}-${detail.value}`}>
							{renderDetail(detail, classes)}
						</Fragment>
					))}
				</div>
				{description ? (
					<div className={classes.description()}>{description}</div>
				) : null}
				{links && links.items.length > 0 ? (
					<div className={classes.footer()}>
						{links.items.map((link) => (
							<div key={link.content.id}>{RenderBlock(link)}</div>
						))}
					</div>
				) : null}
			</div>
		</div>
	);
}
